import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { ListBuilderContext } from './list-builder.context';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ListBuilderService } from '../list-creation/service/list-builder.service';
import { AlertService } from '../alert/alert.service';
import { ListEntry } from './model/list.model';
import { ModalConfirmComponent } from '../modal/modal-confirm.component';
import { TranslateService } from '@ngx-translate/core';
import { VIEW_NAMES_PERMISSION, VIEW_PEDIGREE_INFORMATION_PERMISSION } from '../auth/permissions';
import { Principal } from '../auth/principal.service';
import { Router } from '@angular/router';
import { GermplasmListBuilderReorderEntriesDialogComponent } from '../reorder-entries/germplasm-list-builder-reorder-entries-dialog.component';
import { GermplasmDetailsPopupService } from '../../germplasm-details/germplasm-details-popup.service';
import { InventoryDetailsPopupService } from '../../germplasm-manager/inventory/details/inventory-details-popup.service';
import { JhiEventManager } from '../core-utils';
import { SubscriptionsManager } from '../util/subscriptions-manager';
import { MAX_NAME_DISPLAY_SIZE } from '../../app.constants';

@Component({
    selector: 'jhi-list-builder',
    templateUrl: './list-builder.component.html'
})
export class ListBuilderComponent implements OnInit, OnDestroy {

    subscriptionManager = new SubscriptionsManager();

    @Input() openListOnSave = false;

    CROSS = 'CROSS';
    NAMES = 'NAMES';
    MAX_NAME_DISPLAY_SIZE = MAX_NAME_DISPLAY_SIZE;
    HIDDEN_COLUMNS = ['_internal_id', 'entryNo'];
    data: ListEntry[] = [];
    page = 1;

    // { <data-index>: boolean }
    selectedItems = {};
    isSelectAllPages = false;
    displayMenuPopup = false;
    lastClickIndex: any;

    rightClickMenuPositionX: number;
    rightClickMenuPositionY: number;

    templateColumnCount = 1;

    constructor(
        public context: ListBuilderContext,
        private modalService: NgbModal,
        private listBuilderService: ListBuilderService,
        private alertService: AlertService,
        private translateService: TranslateService,
        private router: Router,
        private principal: Principal,
        private eventManager: JhiEventManager,
        protected germplasmDetailsPopupService: GermplasmDetailsPopupService,
        protected inventoryDetailsPopupService: InventoryDetailsPopupService,
    ) {
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
    }

    async ngOnInit() {
        this.subscriptionManager.add = this.eventManager.subscribe(GermplasmListBuilderReorderEntriesDialogComponent.LIST_BUILDER_REORDER_EVENT_SUFFIX, (event) => {
            this.data = this.context.data;
            this.alertService.success('list-builder.reorder.success');
            this.selectedItems = {};
        });
    }

    isSelected(index) {
        return this.selectedItems[index];
    }

    showRightClickMenu($event) {
        if (this.data && this.data.length > 0) {
            $event.preventDefault();
            this.displayMenuPopup = true;
            this.rightClickMenuPositionX = $event.clientX;
            this.rightClickMenuPositionY = $event.clientY;
        }
    }

    getRightClickMenuStyle() {
        return {
            position: 'fixed',
            left: `${this.rightClickMenuPositionX}px`,
            top: `${this.rightClickMenuPositionY}px`
        };
    }

    toggleSelect($event, index, internal_id, checkbox = false) {
        if (!$event.ctrlKey && !checkbox) {
            this.selectedItems = {};
        }
        let ids;
        if ($event.shiftKey) {
            const max = Math.max(this.lastClickIndex, index) + 1 + this.pageOffset(),
                min = Math.min(this.lastClickIndex, index) + this.pageOffset();
            ids = this.data.slice(min, max).map((g) => g.internal_id);
        } else {
            ids = [internal_id];
            this.lastClickIndex = index;
        }
        const isClickedItemSelected = this.selectedItems[internal_id];
        for (const id of ids) {
            if (isClickedItemSelected) {
                delete this.selectedItems[id];
            } else {
                this.selectedItems[id] = true;
            }
        }
    }

    isPageSelected() {
        const pageItemIds = this.getPageItemIds();
        return this.size(this.selectedItems) && pageItemIds.every((itemId) => this.selectedItems[itemId]);
    }

    onSelectPage() {
        const pageItemIds = this.getPageItemIds();
        if (this.isPageSelected()) {
            // remove all items
            pageItemIds.forEach((itemId) => delete this.selectedItems[itemId]);
        } else {
            // check remaining items
            pageItemIds.forEach((itemId) => this.selectedItems[itemId] = true);
        }
    }

    getPageItemIds(): any[] {
        if (!(this.data && this.data.length)) {
            return [];
        }
        return this.data.slice(this.pageOffset(), this.page * this.context.pageSize)
            .map((row) => row.internal_id);
    }

    pageOffset() {
        return (this.page - 1) * this.context.pageSize;
    }

    onSelectAllPages() {
        this.isSelectAllPages = !this.isSelectAllPages;
        this.selectedItems = {};
    }

    getHeaderDisplay(header: string) {
        return this.translateService.instant('search-germplasm.column.' + header.toLowerCase().replace(/[_ ]/g, '.'));
    }
    headers() {
        if (!(this.data && this.data.length)) {
            return [];
        }
        const hiddenColumns = [...this.HIDDEN_COLUMNS];
        if (!this.principal.hasAnyAuthorityDirect(VIEW_NAMES_PERMISSION) || !this.principal.hasAnyAuthorityDirect(VIEW_PEDIGREE_INFORMATION_PERMISSION)) {
            hiddenColumns.push(this.CROSS);
        }
        if (!this.principal.hasAnyAuthorityDirect(VIEW_NAMES_PERMISSION)) {
            hiddenColumns.push(this.NAMES);
        }
        return Object.keys(this.data[0]).filter((header) => hiddenColumns.indexOf(header) === -1);
    }

    size(obj) {
        return Object.keys(obj).length;
    }

    drop(event: CdkDragDrop<any>) {
        if (event.previousContainer === event.container) {
            moveItemInArray(this.data,
                event.previousIndex + this.pageOffset(),
                event.currentIndex + this.pageOffset());
        } else if (this.data.length > 0) {
            this.data.push(...this.context.data);
        } else {
            this.data = this.context.data;
        }
    }

    async deleteSelected() {
        if (!this.validateSelection()) {
            return;
        }

        const modalRef = this.modalService.open(ModalConfirmComponent);
        modalRef.componentInstance.message = this.translateService.instant('list-builder.delete.confirm');
        try {
            await modalRef.result;
        } catch (e) {
            return;
        }
        if (this.isSelectAllPages) {
            this.data = [];
        } else {
            this.data = this.data.filter((row) => !this.selectedItems[row.internal_id]);
        }
        this.selectedItems = {};
    }

    async reorderSelectedEntries() {
        if (!this.validateSelection()) {
            return;
        }

        this.context.data = this.data;

        const reOrderEntriesModal = this.modalService.open(GermplasmListBuilderReorderEntriesDialogComponent as Component);
        reOrderEntriesModal.componentInstance.selectedEntries = Object.keys(this.selectedItems).map((id) => Number(id)).filter((id) => this.selectedItems[id] === true);
    }

    async deleteDuplicates() {
        const modalRef = this.modalService.open(ModalConfirmComponent);
        modalRef.componentInstance.message = this.translateService.instant('list-builder.delete.duplicates.confirm');
        try {
            await modalRef.result;
        } catch (e) {
            return;
        }
        const map = this.data.reduce((_map, row) => {
            _map[row[this.listBuilderService.getIdColumnName()]] = row;
            return _map;
        }, {});
        this.data = Object.values(map);
        this.selectedItems = {};
    }

    async reset() {
        const modalRef = this.modalService.open(ModalConfirmComponent);
        modalRef.componentInstance.message = this.translateService.instant('list-builder.reset.confirm');
        try {
            await modalRef.result;
        } catch (e) {
            return;
        }
        this.data = [];
        this.selectedItems = {};
    }

    save() {
        this.listBuilderService.openSaveModal(this.data)
            .then((res) => this.onSuccess(res),
                () => {
                    // on reject - noop
                }
            );
    }

    isRegularColumn(header): boolean {
        return header !== this.NAMES && header !== this.CROSS
            && header !== 'GID' && header !== 'DESIGNATION' && header !== 'LOTS';
    }

    async onSuccess(response) {
        if (this.openListOnSave && response) {
            await this.router.navigate([`/germplasm-list/list/${response.listId}`], {
                queryParams: {
                    listId: response.listId,
                    listName: response.listName
                },
                queryParamsHandling: 'merge'
            });
        }

        this.context.visible = false;
        this.data = [];

    }

    private validateSelection() {
        if (!this.selectedItems || (!this.isSelectAllPages && this.size(this.selectedItems) === 0)) {
            this.alertService.error('list-builder.selection.empty');
            return false;
        }
        return true;
    }

    @HostListener('document:click')
    documentClick(): void {
        this.displayMenuPopup = false;
    }
}